import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { getAccessToken, ACCESS_TOKEN_KEY } from "../../../core/auth/storage";
import { authApi } from "../api/authApi";
import {
  AuthContextValue,
  AuthMeResponse,
  AuthUser,
  LoginPayload,
  Membership,
  PermissionCode,
  PermissionGrant,
  Scope,
} from "../model/auth.types";
import {
  buildPermissionCode,
  createPermissionSet,
  flattenPermissions,
  getPermissionScopeFromPermissions,
  hasDomainAccessFromMemberships,
  hasPermissionFromSet,
} from "features/access-control/utils/access-control.helpers";

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message || "Request failed";
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

function emptySession() {
  return {
    user: null as AuthUser | null,
    memberships: [] as Membership[],
    permissions: [] as PermissionGrant[],
    permissionSet: new Set<PermissionCode>(),
  };
}

function normalizeMemberships(value: unknown): Membership[] {
  if (!Array.isArray(value)) return [];

  return value
    .filter(
      (item): item is Membership => Boolean(item) && typeof item === "object",
    )
    .map((membership) => {
      const rawRoles = Array.isArray(membership.roles) ? membership.roles : [];
      const roles = rawRoles.map((role) => {
        if (typeof role === "string") {
          return {
            id: undefined,
            code: role,
            name: undefined,
          };
        }

        if (role && typeof role === "object" && "code" in role) {
          return {
            id: role.id,
            code: String(role.code ?? ""),
            name: role.name,
          };
        }

        return {
          id: undefined,
          code: String(role),
          name: undefined,
        };
      });

      const permissions = Array.isArray(membership.permissions)
        ? membership.permissions.filter(
            (permission): permission is PermissionGrant =>
              Boolean(permission) &&
              typeof permission === "object" &&
              typeof permission.code === "string" &&
              typeof permission.scope === "string",
          )
        : [];

      return {
        ...membership,
        domain: membership.domain,
        roles,
        permissions,
      };
    });
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [permissions, setPermissions] = useState<PermissionGrant[]>([]);
  const [permissionSet, setPermissionSet] = useState<Set<PermissionCode>>(
    new Set(),
  );
  const [isLoading, setIsLoading] = useState(true);

  const applySession = useCallback((session: AuthMeResponse) => {
    const safeMemberships = normalizeMemberships(session.memberships);
    const flattenedPermissions = flattenPermissions(safeMemberships);

    setUser(session.user);
    setMemberships(safeMemberships);
    setPermissions(flattenedPermissions);
    setPermissionSet(createPermissionSet(flattenedPermissions));
  }, []);

  const clearSession = useCallback(() => {
    const empty = emptySession();
    setUser(empty.user);
    setMemberships(empty.memberships);
    setPermissions(empty.permissions);
    setPermissionSet(empty.permissionSet);
  }, []);

  const refreshSession = useCallback(async (): Promise<void> => {
    const token = getAccessToken();

    if (!token) {
      clearSession();
      setIsLoading(false);
      return;
    }

    try {
      const authMe = await authApi.getMe();
      applySession(authMe);
    } catch (error) {
      console.error(getErrorMessage(error) || "Fetching auth session failed");
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      clearSession();
    } finally {
      setIsLoading(false);
    }
  }, [applySession, clearSession]);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  const login = useCallback(
    async (payload: LoginPayload): Promise<void> => {
      const loginRes = await authApi.login(payload);
      const storage = payload.remember ? localStorage : sessionStorage;
      storage.setItem(ACCESS_TOKEN_KEY, loginRes.access_token);
      await refreshSession();
    },
    [refreshSession],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error(getErrorMessage(error) || "Logout failed");
    } finally {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      clearSession();
    }
  }, [clearSession]);

  const hasDomainAccess = useCallback(
    (domain: Membership["domain"]) =>
      hasDomainAccessFromMemberships(memberships, domain),
    [memberships],
  );

  const hasPermission = useCallback(
    (permission: PermissionCode) =>
      hasPermissionFromSet(permissionSet, permission),
    [permissionSet],
  );

  const can = useCallback(
    ({
      domain,
      resource,
      action,
    }: {
      domain: Membership["domain"];
      resource: string;
      action: string;
    }) => {
      return (
        hasDomainAccess(domain) &&
        hasPermission(buildPermissionCode({ domain, resource, action }))
      );
    },
    [hasDomainAccess, hasPermission],
  );

  const getPermissionScope = useCallback(
    (permission: PermissionCode): Scope | undefined =>
      getPermissionScopeFromPermissions(permissions, permission),
    [permissions],
  );

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      user,
      memberships,
      permissions,
      permissionSet,
      isAuthenticated: Boolean(user),
      isLoading,
      login,
      logout,
      refreshSession,
      hasDomainAccess,
      hasPermission,
      can,
      getPermissionScope,
    }),
    [
      user,
      memberships,
      permissions,
      permissionSet,
      isLoading,
      login,
      logout,
      refreshSession,
      hasDomainAccess,
      hasPermission,
      can,
      getPermissionScope,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
